// Guardas de navegación del panel de organizador, gateadas por `perms.tickets` (sesión 6 de
// instrucSesiones, cambio organizer-permission-guards). El back sigue siendo la barrera real
// (sus rutas se guardan con `requirePermiso`, ver el repo del back): esto es UX, para no mostrar
// ni dejar entrar por URL directa a una sección que el back de todos modos va a rechazar.
//
// El dueño, y cualquier sesión sin `tipo === "empleado"` (por ejemplo un login propio de Tickets,
// que no viene de Zoco), ve todo, igual que hoy: el filtro solo corre para empleados.
//
// El otro filtro, para todos, son las tarjetas del negocio (tarjetasDelNegocio): solo afecta el
// destino de `primeraRutaPermitida`; las guardas lo aplican aparte.

import { negocioMuestraRuta } from "./tarjetasDelNegocio";

/**
 * Ruta del panel → módulo de `perms.tickets` que la gobierna (o lista de módulos: alcanza con uno). `"dueno"` es un módulo especial:
 * secciones que un empleado nunca ve, tenga las claves que tenga.
 */
const RUTA_A_MODULO = {
  "/organizer": "dueno",
  "/organizer/asistencia": "dueno",
  "/organizer/sellers": "dueno",
  "/organizer/scanners": "dueno",
  "/organizer/payments": "dueno",
  "/organizer/notificaciones": "dueno",
  "/organizer/events": "eventos",
  "/organizer/coupons": "eventos",
  "/organizer/sponsors": "eventos",
  "/organizer/presencial": "entradas",
  "/organizer/gifts": "entradas",
  "/organizer/orders": "contable",
  "/organizer/reports": "contable",
  "/organizer/marketing": "marketing",
  "/organizer/reservas": "reservas",
  "/organizer/canchas": "reservas",
  "/organizer/canchas/cumpleanos": "reservas",
  "/organizer/estadisticas/canchas": "reservas",
  "/organizer/turnos": "turnos",
  // Same key as Mi salón: a linked professional (an employee with `turnos`) opens it and the back
  // narrows the numbers to their own.
  "/organizer/estadisticas/beauty": "turnos",
  // Fichas de cliente compartidas por el negocio: las ve quien lee salón o reservas (canchas y mesas).
  "/organizer/clientes": ["turnos", "reservas"],
};

const CLAVES_POR_LARGO = Object.keys(RUTA_A_MODULO).sort((a, b) => b.length - a.length);

/** El módulo que gobierna `ruta` (por el prefijo mapeado más largo), o `null` si no está en la tabla. */
export const moduloDeRuta = (ruta) => {
  if (typeof ruta !== "string" || !ruta) return null;
  const path = ruta.split("?")[0];
  // "/organizer" es el dashboard (índice exacto), no un paraguas de todo lo que cuelga debajo:
  // solo matchea como prefijo de subrutas el resto de las claves.
  const clave = CLAVES_POR_LARGO.find((k) => path === k || (k !== "/organizer" && path.startsWith(`${k}/`)));
  return clave ? RUTA_A_MODULO[clave] : null;
};

/**
 * Si `auth` puede ver `ruta`. Solo filtra `tipo === "empleado"`: el dueño y cualquier otra sesión
 * (login propio de Tickets, SELLER) ven todo, sin cambios respecto de hoy.
 */
export const puedeVerRuta = (auth, ruta) => {
  if (auth?.user?.tipo !== "empleado") return true;

  const modulo = moduloDeRuta(ruta);
  if (modulo === "dueno") return false;
  if (!modulo) return true;

  const tickets = auth?.user?.perms?.tickets;
  const alguno = (Array.isArray(modulo) ? modulo : [modulo]).some((m) => tickets && Object.prototype.hasOwnProperty.call(tickets, m));
  return Boolean(alguno);
};

// Orden de preferencia para el primer módulo habilitado de un empleado, el mismo con el que se
// arma ORGANIZER_NAV_LINKS en organizer/Layout.jsx (sin las secciones dueño-solo).
const ORDEN_RUTAS_EMPLEADO = [
  "/organizer/presencial",
  "/organizer/events",
  "/organizer/coupons",
  "/organizer/gifts",
  "/organizer/reports",
  "/organizer/marketing",
  "/organizer/sponsors",
  "/organizer/reservas",
  "/organizer/canchas",
  "/organizer/estadisticas/canchas",
  "/organizer/turnos",
  "/organizer/estadisticas/beauty",
  "/organizer/clientes",
  // Compartidas por todos los negocios (sin tarjeta): el último refugio de un dueño cuyo negocio
  // no usa ninguna sección de Tickets. Un empleado no las ve (módulo "dueno").
  "/organizer/sellers",
  "/organizer/payments",
];

/**
 * A dónde mandar a quien se le negó una ruta: la primera sección que su `perms.tickets` permite y
 * que las tarjetas del negocio muestran. Si no hay ninguna, presencial (donde ya caían hoy los roles
 * sin acceso) siempre que el negocio la muestre; si tampoco, `null`: no hay a dónde mandarlo y el
 * llamador tiene que mostrar un aviso en vez de redirigir (así no hay loops).
 */
export const primeraRutaPermitida = (auth) => {
  const visible = (ruta) => negocioMuestraRuta(auth?.negocio, ruta);
  const encontrada = ORDEN_RUTAS_EMPLEADO.find((ruta) => visible(ruta) && puedeVerRuta(auth, ruta));
  if (encontrada) return encontrada;
  return visible("/organizer/presencial") ? "/organizer/presencial" : null;
};
